/** Persisted tenant slug for dev / non-subdomain hosts (cookie + localStorage). */

export const TENANT_SLUG_STORAGE_KEY = 'slide_rule_tenant_slug';
const TENANT_SLUG_COOKIE = 'slide_rule_tenant_slug';
const COOKIE_MAX_AGE_SEC = 365 * 24 * 60 * 60;

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') {
    return null;
  }
  const prefix = `${encodeURIComponent(name)}=`;
  const parts = document.cookie.split(';');
  for (const part of parts) {
    const t = part.trim();
    if (t.startsWith(prefix)) {
      try {
        return decodeURIComponent(t.slice(prefix.length));
      } catch {
        return t.slice(prefix.length);
      }
    }
  }
  return null;
}

function writeCookie(name: string, value: string | null): void {
  if (typeof document === 'undefined') {
    return;
  }
  if (value === null || value === '') {
    document.cookie = `${encodeURIComponent(name)}=; Path=/; Max-Age=0; SameSite=Lax`;
    return;
  }
  document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}; Path=/; Max-Age=${COOKIE_MAX_AGE_SEC}; SameSite=Lax`;
}

/** One-time migration: copy localStorage slug to cookie. */
let _storageMigrated = false;

function migrateLocalStorageToCookieOnce(): void {
  if (_storageMigrated || typeof localStorage === 'undefined') {
    return;
  }
  _storageMigrated = true;
  try {
    const fromLs = localStorage.getItem(TENANT_SLUG_STORAGE_KEY)?.trim();
    if (fromLs && !readCookie(TENANT_SLUG_COOKIE)) {
      writeCookie(TENANT_SLUG_COOKIE, fromLs.toLowerCase());
    }
  } catch {
    /* ignore */
  }
}

export function getStoredTenantSlug(): string | null {
  migrateLocalStorageToCookieOnce();
  const fromCookie = readCookie(TENANT_SLUG_COOKIE)?.trim();
  if (fromCookie) {
    return fromCookie.toLowerCase();
  }
  try {
    const v = localStorage.getItem(TENANT_SLUG_STORAGE_KEY)?.trim();
    return v ? v.toLowerCase() : null;
  } catch {
    return null;
  }
}

export function setStoredTenantSlug(slug: string | null): void {
  const normalized = slug?.trim() ? slug.trim().toLowerCase() : null;
  writeCookie(TENANT_SLUG_COOKIE, normalized);
  try {
    if (normalized) {
      localStorage.setItem(TENANT_SLUG_STORAGE_KEY, normalized);
    } else {
      localStorage.removeItem(TENANT_SLUG_STORAGE_KEY);
    }
  } catch {
    /* ignore quota / private mode */
  }
}
